/**
 * @file This file contains utility functions for decoding binary data to text.
 */
import { Either } from 'purify-ts';
/** Error thrown when a file could not be decoded as text. */
export class BinaryFileError extends Error {
    rawBytes;
    /** This is not an interface. */
    _tag;
    /**
     * @param rawBytes The bytes that could not be decoded as text.
     */
    constructor(rawBytes) {
        super('Binary file detected');
        this.rawBytes = rawBytes;
        Object.freeze(this);
    }
}
/**
 * Try to parse an `ArrayBuffer` to a string using various encodings, or throw an error.
 *
 * @param buf The buffer to decode.
 * @param force Whether to throw an error on invalid data instead of using a replacement character.
 * @returns The decoded string.
 * @throws Only when `force` is `false` (the default), this may throw a `TypeError` when
 *     `TextEncoder.decode` would, or a `BinaryFileError` when the input looks like a binary file.
 */
export function buffer(buf, force = false) {
    const fatal = !force;
    try {
        return new TextDecoder('utf8', { fatal }).decode(buf);
    }
    catch (e) {
        if (new Int8Array(buf).includes(0)) {
            throw new BinaryFileError(buf);
        }
        return new TextDecoder('latin1', { fatal }).decode(buf);
    }
}
/**
 * Try to parse an `ArrayBuffer` to a string using various encodings.
 *
 * @param buf The buffer to decode.
 * @returns Either the decoded string or an error that occurred during decoding.
 */
export function bufferE(buf) {
    return Either.encase(() => buffer(buf, false));
}
